import { Formik } from "formik";
import React, { useState } from "react";
import { Button, Form, FormText, InputGroup, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import * as yup from "yup";
import BankAccount from "../../components/BankAccount";
import FormikCheckbox from "../../components/Forms/FormikCheckbox";
import FormikTextField from "../../components/Forms/FormikTextField";
import LoaderButton from "../../components/LoaderButton";
import config from "../../config";
import useAuth from "../../contexts/useAuth";
import { Constants } from "../../libs/Constants";
import { create, update } from "../../libs/dbFunctions";
import EditContributionButton from "./EditContributionButton";
import { ListBankAccounts } from "./ListBankAccounts";
import SettingLayout from "./settinglayout";

const configObj = config.getConfigObject();
const schema = yup.object({
  phoneNumber: yup.string(),
  allowTextNotifications: yup.boolean(),
  salary: yup.number().min(0),
  email: yup.string().email("A valid email is required"),
  code: yup.string(),
});
const AccountInformation = ({ refreshUser, setLoading }) => {
  const { user: loggedInUser } = useAuth();
  const [codeSent, setCodeSent] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [emailEdit, setEmailEdit] = useState(false);
  const [showAddBankAccount, setShowAddBankAccount] = useState(false);
  const [addingBroker, setAddingBroker] = useState(false);
  const [showVerifyAccount, setShowVerifyAccount] = useState(false);
  const [verifyAccount, setVerifyAccount] = useState(null);
  const [verifyValues, setVerifyValues] = useState({});

  const handleSubmit = async (values) => {
    if (codeSent) {
      await verifyCode(values);
      return;
    }
    if (loggedInUser.email !== values.email) {
      await createEmailChangeRequest(values.email);
      return;
    }
    //update userObj with phone number and allowText Notifications    
    let saveFields = {
      id: loggedInUser.id,
      phoneNumber: values.phoneNumber ? values.phoneNumber : null,
      allowTextNotifications: values.allowTextNotifications,
      salary: Number(values.salary),
    };
    await updateUser(saveFields);
  };
  const updateUser = async (user) => {
    try {
      await update(Constants.dbTypes.USER, user);
      toast.success("Profile updated successfully");
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  };
  const createEmailChangeRequest = async (newEmail) => {
    try {
      // Create email change request
      let response = await create(Constants.dbTypes.CHANGE_EMAIL, {
        newEmail: newEmail,
      });
      setRequestId(response.id);
      setCodeSent(true);
    } catch (e) {
      console.log("e", e);
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  };
  const verifyCode = async (values) => {
    //confirm the the signup with verification code
    try {
      await update(Constants.dbTypes.CHANGE_EMAIL, {
        uuid: requestId,
        otp: values.code,
      });
      toast.success("Your email is updated successfully.");
    } catch (e) {
      console.log("e", e);
      if (
        e.response &&
        e.response.data &&
        e.response.data.error &&
        typeof e.response.data.error == "string"
      ) {
        toast.error(e.response.data.error);
      } else {
        toast.error("Unable to change email");
      }
    }
  };
  const handleSuccess = async (response) => {
    let bankInfo = null;
    if (configObj.bank_integration === "dwolla") {
      let details = response["_links"]["funding-source"]["href"].split("/");
      bankInfo = details[details.length - 1];
    }
    if (
      configObj.bank_integration === "plaid" &&
      "token" in response &&
      response.token
    ) {
      bankInfo = response.token ? response.token : null;
    }
    if (bankInfo) {
      if (loggedInUser.accounts.find((acc) => acc.id === bankInfo)) {
        toast.error(
          "You have already added this bank account. Please select another account"
        );
        return;
      }
      setLoading(true);
      if (addingBroker && !loggedInUser.broker_information) {
        let saveFields = {
          ...loggedInUser,
          broker_information: bankInfo,
        };
        await updateUser(saveFields);
      }
      toast.success("Account added successfully");
      refreshUser();
    }
    setLoading(false);
  };
  const handleChange = (e) => {
    let o = Object.assign({}, verifyValues, {
      [e.target.id]: Number(e.target.value),
    });
    setVerifyValues(o);
  };
  const resendCode = async (e) => {
    setLoading(true);
    //call the email change request update api with resend code set to true;
    try {
      await update(Constants.dbTypes.CHANGE_EMAIL, {
        uuid: requestId,
        resendCode: true
      });
      toast.success("Verification code is resent to your email.");
      setCodeSent(true);
    } catch (e) {
      console.log("e", e);
      if (
        e.response &&
        e.response.data &&
        e.response.data.error &&
        typeof e.response.data.error == "string"
      ) {
        toast.error(e.response.data.error);
      } else {
        toast.error("Unable to resend verification code.");
      }
    }
    setLoading(false);
  }
  const handleVerifyAccount = async () => {
    setShowVerifyAccount(false);
    setLoading(true);
    let saveFields = {
      id: loggedInUser.id,
      account_id: verifyAccount,
      amount1: verifyValues.amount1,
      amount2: verifyValues.amount2,
      companyId: loggedInUser.companyId,
    };
    //verify the account
    try {
      await update(
        Constants.dbTypes.ACCOUNT,
        saveFields,
        loggedInUser.companyId
      );
      toast.info(
        "Verification is in progress. It may take 2-3 days to complete the process."
      );
      await refreshUser();
      setLoading(false);
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  };
  const renderVerifyModal = () => {
    return (
      <Modal
        show={showVerifyAccount}
        onHide={() => setShowVerifyAccount(false)}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="contribution-modal font-secondary"
      >
        <Modal.Header className="p-4" closeButton>
          <Modal.Title>Verify Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group
              size="lg"
              controlId="amount1"
              className="form-group mb-4"
            >
              <div>
                <Form.Label>Amount 1 </Form.Label>
              </div>

              <Form.Control
                required
                size="lg"
                autoFocus
                type="number"
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group
              size="lg"
              controlId="amount2"
              className="form-group mb-4"
            >
              <div>
                <Form.Label>Amount 2 </Form.Label>
              </div>

              <Form.Control
                required
                size="lg"
                autoFocus
                type="number"
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            className="btn btn-primary"
            onClick={handleVerifyAccount}
          >
            Submit
          </Button>
          <Button
            type="button"
            variant="outline-cancel"
            onClick={() => setShowVerifyAccount(false)}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };
  return (
    <SettingLayout title="Account Information">
      <Formik
        validationSchema={schema}
        onSubmit={async (values, { resetForm }) => {
          setLoading(true);
          await handleSubmit(values);
          if (codeSent) {
            resetForm();
            await refreshUser();
            setEmailEdit(false);
            setCodeSent(false);
          }
          setLoading(false);
        }}
        initialValues={{
          email: loggedInUser.email,
          phoneNumber: loggedInUser.phoneNumber,
          allowTextNotifications: loggedInUser.allowTextNotifications,
          salary: loggedInUser.salary,
        }}
        enableReinitialize
      >
        {({ handleSubmit, isValid, isSubmitting, values }) => (
          <form noValidate onSubmit={handleSubmit} className="w-100 px-5 pt-5">
            <FormikTextField
              size="lg"
              controlId="email"
              label="Email"
              type="text"
              disabled={!emailEdit}
              name="email"
              className="mb-4"
            />
            {!codeSent && !emailEdit && (
              <div className="mb-4">
                <span className="form-label font-primary">
                  Want to edit your email?{" "}
                </span>
                <Button variant="link" onClick={() => setEmailEdit(true)}>
                  Edit
                </Button>
              </div>
            )}
            {emailEdit && codeSent && (
              <>
                <FormikTextField
                  size="lg"
                  controlId="code"
                  label="Verification Code"
                  type="text"
                  name="code"
                  className="mb-2"
                />
                <FormText className="text-muted">
                  Please check your email ({values.email}) for the verification
                  code.
                </FormText>
                <div className="d-flex font-secondary account pb-4 align-items-center">
                  Didn’t receive the email?
                <Button
                    variant="link"
                    onClick={resendCode}
                    className="secondary ps-1"
                  >
                    Resend
                </Button>
                </div>
              </>
            )}
            {!emailEdit && (
              <>
                <FormikTextField
                  size="lg"
                  controlId="phoneNumber"
                  label="Phone"
                  type="text"
                  name="phoneNumber"
                  className="mb-2"
                />

                <FormikCheckbox
                  controlId="allowTextNotifications"
                  name="allowTextNotifications"
                  className="mb-0"
                  label={
                    <span className="form-label font-primary">
                      Allow Text Notifications
                    </span>
                  }
                />

                <hr className="greyHr w-100" />
                <ListBankAccounts
                  accounts={loggedInUser?.accounts}
                  onVerify={(account) => {
                    setVerifyAccount(account.id);
                    setShowVerifyAccount(true);
                  }}
                />

                {loggedInUser?.accounts && loggedInUser?.accounts.length > 0 && (
                  <>
                    <div className="mb-4">
                      <span className="form-label font-primary">
                        Want to add a{" "}
                        <a
                          onClick={() => {
                            setAddingBroker(false);
                            setShowAddBankAccount(true);
                          }}
                          className="text-secondary fw-bold font-size-4 cursor-pointer"
                        >
                          Bank Account
                        </a>{" "}
                        or{" "}
                        <a
                          onClick={() => {
                            setAddingBroker(true);
                            setShowAddBankAccount(true);
                          }}
                          className="text-secondary fw-bold font-size-4 cursor-pointer"
                        >
                          Brokerage Account
                        </a>
                        ?
                      </span>
                    </div>

                    <div className="mb-4">
                      <span className="form-label font-primary">
                        Want to edit your primary bank account? Email us at{" "}
                      </span>
                      <a
                        size="sm"
                        className="text-secondary fw-bold font-size-4 cursor-pointer"
                        href="mailto:help@taabl.co"
                      >
                        help@taabl.co
                      </a>
                    </div>
                  </>
                )}
                {showVerifyAccount && renderVerifyModal()}
                {showAddBankAccount && (
                  <BankAccount
                    title={
                      addingBroker
                        ? "Add Brokerage account"
                        : "Add Bank account"
                    }
                    show={showAddBankAccount}
                    onHide={(result, response) => {
                      setShowAddBankAccount(false);
                      if (response) {
                        if (!result) {
                          toast.error(response);
                        } else {
                          handleSuccess(response);
                        }
                      }
                    }}
                    userObj={loggedInUser}
                  ></BankAccount>
                )}
                <hr className="greyHr w-100" />

                <FormikTextField
                  size="lg"
                  controlId="salary"
                  label="Salary"
                  type="number"
                  name="salary"
                  className="mb-4"
                  inputGroupPrepend={
                    <InputGroup.Text id="basic-addon2">$</InputGroup.Text>
                  }
                />
              </>
            )}

            <LoaderButton
              block
              type="submit"
              variant="primary"
              size="lg"
              text="Next"
              className="mb-2"
              isLoading={isSubmitting}
              disabled={!isValid || isSubmitting}
            >
              Save
            </LoaderButton>
            <hr className="greyHr w-100" />
          </form>
        )}
      </Formik>
      {!emailEdit && (
        <div className="ps-5 pb-5 d-flex align-items-center">
          <span className="mt-2 form-label font-primary">
            Want to edit your contribution amount?
          </span>
          <EditContributionButton />
        </div>
      )}
    </SettingLayout>
  );
};

export default AccountInformation;
